import type { ReactElement } from 'react'
import type { SuggesterStation } from './types'
import { defaultStrings } from './strings'
import { formatDistance } from './distance'
import styles from './SuggestionRow.module.css'

/**
 * Props for {@link SuggestionRow}.
 */
export interface SuggestionRowProps {
  /** Station rendered by this row. */
  readonly station: SuggesterStation
  /** Called with the station when the row body is activated. */
  readonly onSelect: (station: SuggesterStation) => void
  /**
   * Called when the trailing status composite of a Closed / Advisory row
   * is activated — opens Disruption Details (spec §5). Ignored on
   * Default / Nearby rows (no status composite). Optional so call sites
   * that don't surface Details (e.g. the offline phase, where status is
   * always absent) can omit it.
   */
  readonly onDisruptionTap?: (station: SuggesterStation) => void
  /** "Station closed" badge label. @defaultValue {@link defaultStrings.stationClosed} */
  readonly closedLabel?: string
  /** "Service alert" badge label. @defaultValue {@link defaultStrings.serviceAlert} */
  readonly advisoryLabel?: string
}

/** Resolved display variant of a row — mirrors iOS `SuggestionRow.Variant`. */
type RowVariant = 'default' | 'closed' | 'advisory' | 'nearby'

/**
 * Maximum number of colour bars drawn in the multi-line icon; a station
 * serving more shows the first `MAX_BARS` bars + a "+K" count
 * (K = `lines.length − MAX_BARS`) so the stack never overflows the 64px
 * row. Cross-platform contract — iOS `SuggestionRow.maxBars` / Android
 * mirror this cap.
 */
const MAX_BARS = 4

/**
 * Resolve the row variant from the station shape (spec §3 / §13):
 * - `status.severity === 'closure'` (or the schema `closed` mirror) → Closed;
 * - `status.severity === 'advisory'` → Advisory;
 * - `distanceMeters` set → Nearby;
 * - otherwise → Default.
 *
 * Closed/Advisory win over Nearby — a disrupted Nearby row still shows
 * its status (the distance is dropped, matching iOS where the composite
 * occupies the trailing slot).
 */
function resolveVariant(station: SuggesterStation): RowVariant {
  if (station.status?.severity === 'closure' || station.closed === true) return 'closed'
  if (station.status?.severity === 'advisory') return 'advisory'
  if (station.distanceMeters !== undefined) return 'nearby'
  return 'default'
}

/**
 * A single tappable station row inside a {@link SuggestionSection}.
 *
 * The row body is a real `<button>` so it gets native keyboard
 * activation (Enter / Space) and focus for free. Carries the spec a11y
 * id `suggester-row-<station-id>`.
 *
 * Four variants (spec §3 / §4), driven by {@link resolveVariant}:
 * - **Default** — line-colour ring + name + line name.
 * - **Closed** (`status.severity 'closure'` / `closed`) — dimmed ring +
 *   "Station closed" + circle-slash + chevron.
 * - **Advisory** (`status.severity 'advisory'`) — Default + a yellow
 *   "Service alert" + warning triangle + chevron; the row stays fully
 *   selectable (icon NOT dimmed).
 * - **Nearby** (`distanceMeters` set) — Default + trailing distance label.
 *
 * **Tap targets (spec §5 — mirrors iOS PM regression R2):** Closed /
 * Advisory rows carry a **separate** trailing status composite (label +
 * icon + chevron) wrapped in one wide `<button>` with a11y id
 * `suggester-row-<id>-status-tap`; any tap inside opens Disruption
 * Details. The row body sits in its own button so station selection and
 * Details are independent affordances. Default / Nearby rows have no
 * composite — the body is the only tap region.
 */
export function SuggestionRow({
  station,
  onSelect,
  onDisruptionTap,
  closedLabel = defaultStrings.stationClosed,
  advisoryLabel = defaultStrings.serviceAlert,
}: SuggestionRowProps): ReactElement {
  const variant = resolveVariant(station)
  const closed = variant === 'closed'
  const hasStatusTap = variant === 'closed' || variant === 'advisory'

  // Multi-line stations (>1 line serving the same physical station) get a
  // bar-stack icon + a joined subtitle; single-line stations render exactly
  // as before (ring + single line name). Mirrors iOS `SuggestionRow`.
  const lines = station.lines ?? []
  const multiLine = lines.length > 1
  const overflow = lines.length - MAX_BARS
  const subtitle = multiLine ? lines.map((line) => line.name).join(' · ') : station.lineName

  return (
    <div className={styles.row} data-closed={closed}>
      <button
        type="button"
        className={styles.body}
        data-testid={`suggester-row-${station.id}`}
        data-closed={closed}
        onClick={() => onSelect(station)}
      >
        <span className={styles.iconBox} aria-hidden>
          {multiLine ? (
            <span className={styles.bars} data-line-bars>
              {lines.slice(0, MAX_BARS).map((line) => (
                <span
                  key={line.id}
                  className={styles.bar}
                  data-line-bar
                  style={{ backgroundColor: line.hexColor }}
                />
              ))}
              {overflow > 0 && <span className={styles.barsOverflow}>{`+${overflow}`}</span>}
            </span>
          ) : (
            <span
              className={styles.ring}
              data-line-ring
              style={{ borderColor: station.lineHexColor }}
            />
          )}
        </span>
        <span className={styles.labels}>
          <span className={styles.name}>{station.name}</span>
          {subtitle !== undefined && <span className={styles.lineName}>{subtitle}</span>}
        </span>
        {variant === 'nearby' && (
          <span className={styles.distance}>
            {formatDistance(station.distanceMeters as number)}
          </span>
        )}
      </button>

      {hasStatusTap && (
        <button
          type="button"
          className={`${styles.statusTap} ${closed ? styles.statusClosed : styles.statusAdvisory}`}
          data-testid={`suggester-row-${station.id}-status-tap`}
          aria-label={closed ? closedLabel : advisoryLabel}
          onClick={() => onDisruptionTap?.(station)}
        >
          {closed ? (
            <>
              <span className={styles.statusText}>{closedLabel}</span>
              <NoSignIcon />
            </>
          ) : (
            <>
              <span
                className={styles.statusText}
                data-testid={`suggester-row-${station.id}-advisory`}
              >
                {advisoryLabel}
              </span>
              <WarningTriangleIcon />
            </>
          )}
          <ChevronRightIcon />
        </button>
      )}
    </div>
  )
}

/** Circle-with-slash glyph (Closed badge) — SF Symbol `nosign` analogue. */
function NoSignIcon(): ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden focusable="false" fill="none">
      <circle cx="7" cy="7" r="5.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.4 3.4 L10.6 10.6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}

/** Warning-triangle glyph (Advisory badge) — SF Symbol `exclamationmark.triangle`. */
function WarningTriangleIcon(): ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden focusable="false" fill="none">
      <path
        d="M7 1.6 L13 12.4 H1 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M7 5.4 V8.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="7" cy="10.4" r="0.7" fill="currentColor" />
    </svg>
  )
}

/** Trailing chevron — SF Symbol `chevron.right`; opens Disruption Details. */
function ChevronRightIcon(): ReactElement {
  return (
    <svg
      className={styles.chevron}
      width="8"
      height="13"
      viewBox="0 0 8 13"
      aria-hidden
      focusable="false"
      fill="none"
    >
      <path
        d="M1.5 1 L6.5 6.5 L1.5 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
